import {ReactNode} from 'react';

import {
  ThemeProviderWithoutClassic as ConvUIThemeProviderWithoutClassic,
  MessagePartComponentProvider,
  type Theme,
} from '@stripe-internal/conversations-ui';
import {InlineDataComponentRegistry} from '@stripe-internal/inline-data';
import type {MessagePartComponentRegistry} from '@stripe-internal/conversations-ui';
import type {Observability} from '@stripe-internal/conversations-client';

import {AppCloseHandler, AppConfig, AppProfile} from '../../core/types';
import {ClientFetch} from '../../core/lib/util/clientFetch';
import {ParentMessageBus} from '../../core/lib/parentMessageBus/ParentMessageBus';
import {ThreadsAppProvider} from '../app/ThreadsAppProvider';
import {ThreadsConversationsUIFlagsProvider} from './ThreadsConversationsUIFlagsProvider';
import {ThreadsConversationsUIProviders} from './ThreadsConversationsUIProviders';
import {ThreadsInlineDataProvider} from './ThreadsInlineDataProvider';
import {ThreadsMessagePartHandlers} from './ThreadsMessagePartHandlers';

type Props = {
  config: AppConfig;
  profile: AppProfile;
  clientFetch?: ClientFetch;
  parentMessageBus?: ParentMessageBus;
  inlineDataComponents?: InlineDataComponentRegistry;
  messagePartComponents?: MessagePartComponentRegistry;
  children: ReactNode;
  onClose?: AppCloseHandler;
  observability?: Partial<Observability>;
  theme?: Theme;
  themeOverrides?: Partial<Theme>;
  withHistory?: boolean;
};

export function ThreadsAssistantProviders({
  config,
  profile,
  clientFetch,
  parentMessageBus,
  inlineDataComponents,
  messagePartComponents,
  onClose,
  observability,
  children,
  theme,
  themeOverrides,
  withHistory = true,
}: Props) {
  return (
    <ConvUIThemeProviderWithoutClassic value={theme} overrides={themeOverrides}>
      <ThreadsAppProvider
        config={config}
        profile={profile}
        clientFetch={clientFetch}
        parentMessageBus={parentMessageBus}
        onClose={onClose}
        observability={observability}
        withHistory={withHistory}
      >
        <ThreadsConversationsUIProviders>
          <ThreadsConversationsUIFlagsProvider>
            <ThreadsMessagePartHandlers>
              <MessagePartComponentProvider value={messagePartComponents}>
                <ThreadsInlineDataProvider components={inlineDataComponents}>
                  {children}
                </ThreadsInlineDataProvider>
              </MessagePartComponentProvider>
            </ThreadsMessagePartHandlers>
          </ThreadsConversationsUIFlagsProvider>
        </ThreadsConversationsUIProviders>
      </ThreadsAppProvider>
    </ConvUIThemeProviderWithoutClassic>
  );
}
